import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import AdbreaksAcceptedFormatTags from "./AdbreaksAcceptedFormatTags";
import AdBreaksFormHeader from "./AdBreaksFormHeader";
import { DRAG_TYPE } from "../../../constants/dragTypes";
import { AD_BREAK_TYPES } from "../../../constants/fast";
import Block from "../../block/Block";
import DropzoneMediaContainer from "../../dropzoneList/DropzoneListContainer";
import Spinner from "../../utils/Spinner";
import DropzoneMediaItem from "../dropzoneList/DropzoneMediaItem";

import type { JSX } from "react";

import type { AdBreakTypes } from "../../../constants/fast";
import type { MediaFormat } from "../../../constants/media";
import type { KeyWithTranslation } from "../../../types/common";
import type {
  AdBreakDropzoneMedia,
  AdBreakListByType,
} from "../../../types/fast";

const { ADPODS, PRE_ADS_BUMPER, POST_ADS_BUMPER } = AD_BREAK_TYPES;

interface AdBreakStructureBlockProps {
  formatsMetadata: {
    bumperFormat?: KeyWithTranslation<MediaFormat>;
    adpodFormat?: KeyWithTranslation<MediaFormat>;
    promotionFormat?: KeyWithTranslation<MediaFormat>;
  };
  adbreakListByType: AdBreakListByType;
  handleAdBreakChange: (
    adbreakList: AdBreakDropzoneMedia[],
    adbreakType: AdBreakTypes,
  ) => void;
  isLoading: boolean;
}

export default function AdBreakStructureBlock({
  formatsMetadata,
  adbreakListByType,
  handleAdBreakChange,
  isLoading,
}: AdBreakStructureBlockProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const { bumperFormat, adpodFormat, promotionFormat } = formatsMetadata;

  const acceptListBumper = useMemo(
    () =>
      bumperFormat
        ? [`${DRAG_TYPE.MEDIA_LIBRARY_ITEM}-${bumperFormat.key}`]
        : [""],
    [bumperFormat],
  );

  const acceptListAdbreak = useMemo(
    () =>
      adpodFormat && promotionFormat
        ? [adpodFormat, promotionFormat].map(
            (format) => `${DRAG_TYPE.MEDIA_LIBRARY_ITEM}-${format.key}`,
          )
        : [""],
    [adpodFormat, promotionFormat],
  );

  return (
    <Block
      isFullSize
      isScrollableChildren
      title={t("adbreakStructureTitle")}
      headerComponent={
        <AdBreaksFormHeader adbreakListByType={adbreakListByType} />
      }
    >
      {!isLoading && adpodFormat && promotionFormat && bumperFormat ? (
        <div className="flex flex-col gap-[0.625rem]">
          <DropzoneMediaContainer
            acceptList={acceptListBumper}
            classNamesContainer="p-3 h-36"
            initialItemList={adbreakListByType[PRE_ADS_BUMPER].mediaList}
            renderItem={DropzoneMediaItem}
            sizeLimit={1}
            onChange={(adbreakList): void =>
              handleAdBreakChange(adbreakList, PRE_ADS_BUMPER)
            }
            renderFormatedAcceptedList={
              <AdbreaksAcceptedFormatTags
                acceptedMediaFormatList={[bumperFormat]}
              />
            }
          />

          <div className="relative py-[2.5rem]">
            <div className="absolute top-[35px] flex w-full -translate-y-full items-center">
              <div className="bg-muted rounded-[0.46875rem] p-[0.3125rem]">
                {t("dropzoneSeparatorStartTitle")}
              </div>
              <div className="bg-surface-muted relative mx-[0.46875rem] h-[2px] grow" />
            </div>
            <DropzoneMediaContainer
              acceptList={acceptListAdbreak}
              classNamesContainer="p-3 min-h-72"
              initialItemList={adbreakListByType[ADPODS].mediaList}
              renderItem={DropzoneMediaItem}
              onChange={(adbreakList): void =>
                handleAdBreakChange(adbreakList, ADPODS)
              }
              renderFormatedAcceptedList={
                <AdbreaksAcceptedFormatTags
                  acceptedMediaFormatList={[adpodFormat, promotionFormat]}
                />
              }
            />

            <div className="absolute bottom-[35px] flex w-full translate-y-full items-center">
              <div className="bg-muted rounded-[0.46875rem] p-[0.3125rem]">
                {t("dropzoneSeparatorEndTitle")}
              </div>
              <div className="bg-surface-muted relative mx-[0.46875rem] h-[2px] grow" />
            </div>
          </div>

          <DropzoneMediaContainer
            acceptList={acceptListBumper}
            classNamesContainer="p-3 h-36"
            initialItemList={adbreakListByType[POST_ADS_BUMPER].mediaList}
            renderItem={DropzoneMediaItem}
            sizeLimit={1}
            onChange={(adbreakList): void =>
              handleAdBreakChange(adbreakList, POST_ADS_BUMPER)
            }
            renderFormatedAcceptedList={
              <AdbreaksAcceptedFormatTags
                acceptedMediaFormatList={[bumperFormat]}
              />
            }
          />
        </div>
      ) : (
        <div className="flex h-[15rem] items-center justify-center">
          <Spinner />
        </div>
      )}
    </Block>
  );
}
